import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../../utils";
import { Separator } from "../separator/separator";
function ItemGroup({ className, ...props }) {
    return /*#__PURE__*/ _jsx("div", {
        role: "list",
        "data-slot": "item-group",
        className: cn("group/item-group flex flex-col", className),
        ...props
    });
}
function ItemSeparator({ className, ...props }) {
    return /*#__PURE__*/ _jsx(Separator, {
        "data-slot": "item-separator",
        orientation: "horizontal",
        className: cn("my-0", className),
        ...props
    });
}
const itemVariants = cva("group/item [a]:hover:bg-accent/50 focus-visible:border-ring focus-visible:ring-ring/50 [a]:transition-colors flex flex-wrap items-center rounded-md border border-transparent text-sm outline-hidden transition-colors duration-100 focus-visible:ring-[3px]", {
    variants: {
        variant: {
            default: "bg-transparent",
            outline: "border-border",
            muted: "bg-muted/50"
        },
        size: {
            default: "gap-4 p-4 ",
            sm: "gap-2.5 px-4 py-3",
            // Compact row layout for embedded contexts (e.g. entity picker rows)
            // where the outer container already provides the hit-target padding.
            xs: "gap-2.5 p-0"
        }
    },
    defaultVariants: {
        variant: "default",
        size: "default"
    }
});
function Item({ className, variant = "default", size = "default", asChild = false, ...props }) {
    const Comp = asChild ? Slot : "div";
    return /*#__PURE__*/ _jsx(Comp, {
        "data-slot": "item",
        "data-variant": variant,
        "data-size": size,
        className: cn(itemVariants({
            variant,
            size,
            className
        })),
        ...props
    });
}
const itemMediaVariants = cva("flex shrink-0 items-center justify-center gap-2 group-has-data-[slot=item-description]/item:translate-y-0.5 group-has-data-[slot=item-description]/item:self-start [&_svg]:pointer-events-none", {
    variants: {
        variant: {
            default: "bg-transparent",
            icon: "bg-muted size-8 rounded-xs border [&_svg:not([class*='size-'])]:size-4",
            image: "size-10 overflow-hidden rounded-xs [&_img]:size-full [&_img]:object-cover"
        }
    },
    defaultVariants: {
        variant: "default"
    }
});
function ItemMedia({ className, variant = "default", ...props }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-slot": "item-media",
        "data-variant": variant,
        className: cn(itemMediaVariants({
            variant,
            className
        })),
        ...props
    });
}
function ItemContent({ className, ...props }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-slot": "item-content",
        className: cn("flex flex-1 flex-col gap-1 [&+[data-slot=item-content]]:flex-none", className),
        ...props
    });
}
function ItemTitle({ className, ...props }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-slot": "item-title",
        className: cn("flex w-fit items-center gap-2 text-sm font-medium leading-snug", className),
        ...props
    });
}
function ItemDescription({ className, ...props }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-slot": "item-description",
        className: cn("text-muted-foreground line-clamp-2 text-balance text-sm font-normal leading-normal", "[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4", className),
        ...props
    });
}
function ItemActions({ className, ...props }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-slot": "item-actions",
        className: cn("flex items-center gap-2", className),
        ...props
    });
}
function ItemHeader({ className, ...props }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-slot": "item-header",
        className: cn("flex basis-full items-center justify-between gap-2", className),
        ...props
    });
}
function ItemFooter({ className, ...props }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-slot": "item-footer",
        className: cn("flex basis-full items-center justify-between gap-2", className),
        ...props
    });
}
export { Item, ItemMedia, ItemContent, ItemActions, ItemGroup, ItemSeparator, ItemTitle, ItemDescription, ItemHeader, ItemFooter };

//# sourceMappingURL=item.js.map